/**
 * WHAT EACH CHOICE COSTS (D1-24, walk 1 of 2026-10-28).
 *
 * The makrouta gained a Nutella box at 70 beside its Kinder at 65. The page priced the first row, so the shopper read
 * 65 as THE price and met 70 in the cart. Two readings fix that, both of the price the shopper pays (campaigns
 * counted, as the price row counts them — `effectiveVariantPrice`):
 *   - `fromPrice`: while a choice the shopper has not made changes the price, the cheapest row they can still reach,
 *     given what they have picked, said as «من ٦٥ ₪» — read as the shop's cards read their "from"
 *     (`cheapestCampaignPrice`), so the page and the card it was opened from say one price and strike one price;
 *   - `tilePrices`: when a slot's values cost differently, each value's own price, under its name.
 * A row with no price of its own (nor its main row's) is left out: it compares as nothing, never as 0.
 */
import { effectiveVariantPrice as ownPrice } from './productPrice';
import { cheapestCampaignPrice, effectiveVariantPrice } from './shopPromotions';
import { variantPurchaseCap } from './stockCap';

const SLOTS = ['color', 'size', 'style'];

/** The rows that agree with `within` ({ slot: value }) on every slot it names; a null value names nothing. */
function rowsWithin(product, within = {}) {
  const rows = Array.isArray(product?.variants) ? product.variants : [];
  const asked = Object.entries(within || {}).filter(([, value]) => value != null && value !== '');
  return rows.filter((v) => v && asked.every(([slot, value]) => v[slot] === value));
}

/** The price the shopper pays for one row, or null when the row has none. */
function priceOf(variant, product, campaigns) {
  return ownPrice(variant) == null ? null : effectiveVariantPrice(variant, product, campaigns).price;
}

/**
 * Each value of `slot` → the lowest price a row holding it reaches (null when none of its rows has a price): among the rows
 * that can be bought, the sold-out ones only for a value with none left (as `cheapestCampaignPrice` reads a "from").
 */
export function pricesBySlot(product, slot, campaigns, within = {}) {
  const lowest = new Map();
  const lowestBuyable = new Map();
  const keep = (map, value, price) => {
    const before = map.get(value);
    if (!map.has(value) || (price != null && (before == null || price < before))) map.set(value, price);
  };
  rowsWithin(product, within).forEach((v) => {
    const value = v[slot];
    if (value == null || value === '') return;
    const price = priceOf(v, product, campaigns);
    keep(lowest, value, price);
    if (variantPurchaseCap(product, v) !== 0) keep(lowestBuyable, value, price);
  });
  return new Map([...lowest].map(([value, price]) => [value, lowestBuyable.get(value) ?? price]));
}

/** Whether the values of `slot` (within the shopper's other picks) cost differently. */
export function pricesDiffer(product, slot, campaigns, within = {}) {
  const prices = [...pricesBySlot(product, slot, campaigns, within).values()].filter((p) => p != null);
  return new Set(prices).size > 1;
}

/**
 * The «من» price: the cheapest of the rows still open to the shopper — those that agree with `picks` on every slot
 * that is NOT pending — as `{ price, compareAt, discounted }` (that row's price and the price it strikes), when those
 * rows cost differently; null when they cost the same, none has a price, or nothing is pending.
 */
export function fromPrice(product, pendingAxes, campaigns, picks = {}) {
  const pending = new Set(pendingAxes || []);
  if (pending.size === 0) return null;
  const settled = Object.fromEntries(SLOTS.filter((s) => !pending.has(s)).map((s) => [s, picks?.[s] ?? null]));
  const open = rowsWithin(product, settled);
  // With no priced row the card's reading falls back to the whole product's priceFrom, which is not these rows'.
  if (!open.some((v) => ownPrice(v) != null)) return null;
  const { price, compareAt, discounted, priceVaries } = cheapestCampaignPrice({ ...product, variants: open }, campaigns);
  return priceVaries ? { price, compareAt, discounted } : null;
}

/**
 * Each slot's value → price map, or null where the slot's values cost the same. A slot is read within the picks the
 * page lists its values by: the sizes of the picked colour; the styles of the picked colour and size. Every colour is
 * listed whatever the size, so a colour is priced within the size and style the shopper CHOSE (`chosen`), and across
 * them where it has no such row (D1-24 review, M2).
 */
export function tilePrices(product, picks, campaigns, chosen = {}) {
  const priced = (slot, within) => {
    const prices = pricesBySlot(product, slot, campaigns, within);
    return new Set([...prices.values()].filter((p) => p != null)).size > 1 ? prices : null;
  };
  const wide = pricesBySlot(product, 'color', campaigns, {});
  const narrow = pricesBySlot(product, 'color', campaigns, { size: chosen?.size ?? null, style: chosen?.style ?? null });
  const colour = new Map([...wide].map(([value, price]) => [value, narrow.has(value) ? narrow.get(value) : price]));
  return {
    color: new Set([...colour.values()].filter((p) => p != null)).size > 1 ? colour : null,
    size: priced('size', { color: picks?.color ?? null }),
    style: priced('style', { color: picks?.color ?? null, size: picks?.size ?? null }),
  };
}
